import { RefObject, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';
import styled from 'styled-components';
import { spacing } from 'ui/theme';
import { DRAWER_SIZES } from 'ui/workflows/drawer/constants';
import { Takeover } from 'ui/workflows/takeover';
import { useModalContext } from 'utils/contexts/modal';
import { useDynamicContentContext } from '../contexts';
import { useIsConditionalContent } from '../hooks/useIsConditionalContent';
import { useIsConditionalContentDirty } from '../hooks/useIsConditionalContentDirty';
import { useTranslation } from '../hooks/useTranslation';
import { DCB_MODAL } from '../types';
import { ConditionBuilderContent } from './ConditionBuilderContent';
import { Footer } from './Footer';
import { HeaderBarContent } from './HeaderBarContent';
import { DynamicContentBuilderErrorBoundary as ErrorBoundary } from './errorBoundary/DynamicContentBuilderErrorBoundary';

export const DATA_TEST = {
  wrapper: 'dynamic-content-builder-takeover',
};

const BodyContainer = styled.div`
  padding: ${spacing.plusHalf} ${spacing.base};
  height: 100%;
  overflow: auto;
`;

export const BuilderTakeover = () => {
  const { t } = useTranslation();
  const isConditionalContent = useIsConditionalContent();
  const { onClose } = useDynamicContentContext();
  const { showModal } = useModalContext<DCB_MODAL>();
  const isConditionalContentDirty = useIsConditionalContentDirty();
  const [bodyOverflow, setBodyOverflow] = useState('unset');
  const takeover = useRef<HTMLDivElement>(null);

  const handleOnClose = () => {
    if (isConditionalContentDirty) showModal(DCB_MODAL.Confirmation);
    else onClose();
  };

  /**
   * Enter takeover exit process when clicking onto takeover overlay
   */
  useClickAway(
    takeover,
    (e) => {
      if (e.target instanceof HTMLElement && e.target.id === 'takeover-component') {
        handleOnClose();
      }
    },
    ['mousedown'],
  );

  // prompt user about unsaved changes with browser dialog (navigating outside of react-router)
  useEffect(() => {
    if (!isConditionalContentDirty) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isConditionalContentDirty]);

  /**
   * Disables background scrolling on-mount (copied from modal pattern)
   */
  useEffect(() => {
    setBodyOverflow(document.body.style.overflow);
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = bodyOverflow;
    };
  }, []);

  return (
    <Takeover
      title={t('title')}
      HeaderBarContent={<HeaderBarContent />}
      onClose={handleOnClose}
      Footer={<Footer />}
      size={DRAWER_SIZES.LARGE}
      isMainApp={true}
      isOverlay={true}
      isLoading={false}
    >
      <BodyContainer ref={takeover as RefObject<HTMLDivElement>}>
        <ErrorBoundary>
          {isConditionalContent && (
            <div data-test={DATA_TEST.wrapper}>
              <ConditionBuilderContent />
            </div>
          )}
        </ErrorBoundary>
      </BodyContainer>
    </Takeover>
  );
};
